import React, { useState, useEffect, useCallback } from "react";
import { ClipboardList } from "lucide-react";
import DateLabelInput from "../common/forms/DateLabelInput";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import { MdLockReset } from "react-icons/md";
import Pagination from "../common/table/tablepagination";
import { useAuth } from "../../context/AuthContext"; // Auth context

export default function Totalquantityanddutyinbondexbondmaterialwisereport() {
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        startDate: "",
        endDate: "",
    });
    const { logout } = useAuth();

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 5; // number of rows per page

    // Handle date change
    const handleDateChange = (name, value) => {
        setFilters((prev) => ({ ...prev, [name]: value }));
        setCurrentPage(1); // reset to first page
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({ startDate: "", endDate: "" });
        setCurrentPage(1);
        fetchReport(); // load full report
    };

    // Fetch function
    const fetchReport = useCallback(async (start = "", end = "") => {
        setLoading(true);
        let url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.REPORTS.GET_TOTAL_QUANTITY_INBOND_EXBOND_MATERIALWISE}`;
        if (start && end) {
            url += `?start_date=${start}&end_date=${end}`;
        }
        try {
            let response = await fetch(url, { method: "GET", headers: getHeaders(true) });

            // -------------------------
            // 🔥 Handle 401 token expired
            // -------------------------
            if (response.status === 401) {
                console.warn("Access token expired. Attempting refresh...");
                const newToken = await refreshAccessToken();

                if (newToken) {
                    // Retry the same API call with new token
                    response = await fetch(url, { method: "GET", headers: getHeaders(true) });
                } else {
                    console.error("Refresh token failed. Logging out...");
                    logout(); // redirect user
                    return;
                }
            }

            const result = await response.json();
            if (result.status === 200) {
                setReportData(result.data);
            } else {
                setReportData([]);
            }
        } finally {
            setLoading(false);
        }
    }, [logout]);

    // Load full report on first load
    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    // Auto filter load
    useEffect(() => {
        if (filters.startDate && filters.endDate) {
            fetchReport(filters.startDate, filters.endDate);
        }
    }, [filters.startDate, filters.endDate, fetchReport]);

    // Pagination calculations
    const indexOfLastRecord = currentPage * rowsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - rowsPerPage;
    const currentRecords = reportData.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(reportData.length / rowsPerPage);

    const formatIndianNumber = (num) => {
        if (num === null || num === undefined || isNaN(num)) return "0";
        return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };


    return (
        <div className="w-full bg-white p-6 rounded border border-gray-100 shadow-sm hover:shadow-md transition-shadow">

            {/* Header */}
            <div className="flex items-center justify-between border-b border-dashed pb-3 mb-5">
                <div className="flex items-center gap-2">
                    <ClipboardList className="text-indigo-500" size={20} />
                    <h2 className="text-base font-semibold text-gray-900">
                        Total of Quantity & Duty (Material Wise)
                    </h2>
                </div>
            </div>

            {/* Date Filters */}
            <div className="flex items-end gap-4 mb-6 flex-wrap xl:flex-nowrap">
                <div className="w-full sm:w-auto">
                    <DateLabelInput
                        label="Start Date"
                        name="startDate"
                        value={filters.startDate}
                        onChange={(e) => handleDateChange("startDate", e.target.value)}
                    />
                </div>

                <div className="w-full sm:w-auto">
                    <DateLabelInput
                        label="End Date"
                        name="endDate"
                        value={filters.endDate}
                        onChange={(e) => handleDateChange("endDate", e.target.value)}
                    />
                </div>

                {(filters.startDate || filters.endDate) && (
                    <button
                        onClick={resetFilters}
                        className="flex items-center justify-center h-11 w-11"
                        title="Reset Filters"
                    >
                        <MdLockReset size={20} />
                    </button>
                )}
            </div>

            {loading && <div className="text-center py-3">Loading...</div>}

            {!loading && reportData?.length === 0 && (
                <div className="text-gray-500 text-center">No data available</div>
            )}

            {/* === TABLE === */}
            {reportData?.length > 0 && (
                <div className="overflow-hidden">

                    {/* Main Header */}
                    <div className="flex text-xs font-semibold text-gray-700 border-b border-gray-200">
                        <div className="w-[25%] px-4 py-2.5">Material</div>
                        <div className="w-[25%] text-center border-l border-gray-200 py-2.5">Inbond</div>
                        <div className="w-[25%] text-center border-l border-gray-200 py-2.5">Exbond</div>
                        <div className="w-[25%] text-center border-l border-gray-200 py-2.5">Difference</div>
                    </div>

                    {/* Sub Header */}
                    <div className="flex text-[11px] font-medium text-gray-600 border-b border-gray-200">
                        <div className="w-[25%] px-4 py-1"></div>
                        {Array(3).fill(0).map((_, i) => (
                            <div key={i} className="w-[25%] flex text-center border-l border-gray-200">
                                <div className="w-1/2 py-1 border-r border-gray-200">Qty (KG)</div>
                                <div className="w-1/2 py-1">Duty (₹)</div>
                            </div>
                        ))}
                    </div>

                    {/* Rows */}
                    {/* Rows */}
                    <div className="divide-y divide-gray-100 text-table text-gray-800">
                        {currentRecords.map((item, i) => (
                            <div key={i} className="flex items-center">

                                <div className="w-[25%] px-4 py-2.5">{item.material_short_code.toUpperCase()} - {item.material_name ? item.material_name.charAt(0).toUpperCase() + item.material_name.slice(1) : ""}</div>

                                {/* INBOND */}
                                <div className="w-[25%] flex text-center border-l border-gray-200">
                                    <div className="w-1/2 py-2.5 border-r border-gray-200">
                                        {formatIndianNumber(item.data.inbond_data.total_weight_inbond)}
                                    </div>
                                    <div className="w-1/2 py-2.5">
                                        {formatIndianNumber(item.data.inbond_data.total_duty_inbond)}
                                    </div>
                                </div>

                                {/* EXBOND */}
                                <div className="w-[25%] flex text-center border-l border-gray-200">
                                    <div className="w-1/2 py-2.5 border-r border-gray-200">
                                        {formatIndianNumber(item.data.exbond_data.total_weight_exbond)}
                                    </div>
                                    <div className="w-1/2 py-2.5">
                                        {formatIndianNumber(item.data.exbond_data.total_duty_exbond)}
                                    </div>
                                </div>

                                {/* DIFFERENCE */}
                                <div className="w-[25%] flex text-center border-l border-gray-200 font-semibold text-gray-900">
                                    <div className="w-1/2 py-2.5 border-r border-gray-200">
                                        {formatIndianNumber(item.data.difference.difference_weight)}
                                    </div>
                                    <div className="w-1/2 py-2.5">
                                        {formatIndianNumber(item.data.difference.difference_duty)}
                                    </div>
                                </div>

                            </div>
                        ))}
                    </div>
                    {/* Pagination */}
                    {reportData.length > rowsPerPage && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={(page) => setCurrentPage(page)}
                            rowsPerPage={rowsPerPage}
                            totalItems={reportData.length}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
